/**
 * Canada surface routes, external destinations, and the geo opt-out cookie.
 */
import type { Route } from 'next';

// Typed as `Route` so these literals satisfy `next/link` under typedRoutes.
// The MUI `styled(Link)` wrappers that consume them lose Link's route generic,
// so the literal is checked against the generated union rather than inferred —
// same reason the HomepageV3 surfaces cast their hrefs.
export const CANADA_LANDING_ROUTE = '/ca/landing-page' as Route;

/** Route segment every Canadian campaign page lives under. */
export const CANADA_OFFERING_BASE_PATH = '/ca/offering';

/**
 * Canadian campaign pages are per-company: `/ca/offering/<slug>`.
 *
 * Only the slugs listed here resolve — an unrecognised slug 404s rather than
 * serving one offering's terms and disclosures under another offering's URL.
 * Add a slug here when its content lands in `CanadaOfferingPage`.
 */
export const CANADA_OFFERING_SLUGS = ['lambda-labs'] as const;

export type CanadaOfferingSlug = (typeof CANADA_OFFERING_SLUGS)[number];

export const DEFAULT_CANADA_OFFERING_SLUG: CanadaOfferingSlug = 'lambda-labs';

export const canadaOfferingRoute = (slug: string = DEFAULT_CANADA_OFFERING_SLUG) =>
  `${CANADA_OFFERING_BASE_PATH}/${slug}` as Route;

export const isCanadaOfferingSlug = (slug?: string): slug is CanadaOfferingSlug =>
  CANADA_OFFERING_SLUGS.includes(slug as CanadaOfferingSlug);

/** The single offering currently available in Canada. */
export const CANADA_OFFERING_ROUTE = canadaOfferingRoute();

/** Canadian site the wordmark returns to. */
export const STARTENGINE_CA_URL = 'https://www.startengine.ca';

/** US site an existing StartEngine customer opts back out to. */
export const STARTENGINE_COM_URL = 'https://www.startengine.com';

export const CANADA_OPT_OUT_COOKIE = 'se_ca_optout';

/** 30 days, per the agreed opt-out window. */
export const CANADA_OPT_OUT_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

/**
 * Registrable domain for the current host, so the cookie is shared across
 * subdomains (www / apex / preview). `document.cookie` cannot span two
 * different registrable domains, so a cookie written on startengine.ca is NOT
 * visible to startengine.com — see `buildOptOutUrl` for how the other side is
 * told to write its own.
 */
const cookieDomain = (hostname: string): string | undefined => {
  // Bare hosts (localhost, IPs) must not carry a Domain attribute at all.
  if (!hostname.includes('.') || /^[\d.]+$/.test(hostname)) return undefined;
  const parts = hostname.split('.');
  // startengine.co.uk-style multi-part public suffixes are not in play here;
  // both live hosts are <label>.com / <label>.ca.
  return `.${parts.slice(-2).join('.')}`;
};

/**
 * Write `se_ca_optout=1` for 30 days on the current registrable domain.
 * Returns false when there is no document (SSR) so callers can skip navigation
 * side effects in tests.
 */
export function setCanadaOptOutCookie(): boolean {
  if (typeof document === 'undefined') return false;
  const { hostname, protocol } = window.location;
  const domain = cookieDomain(hostname);
  const attrs = [
    `${CANADA_OPT_OUT_COOKIE}=1`,
    'path=/',
    `max-age=${CANADA_OPT_OUT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    ...(domain ? [`domain=${domain}`] : []),
    ...(protocol === 'https:' ? ['Secure'] : []),
  ];
  document.cookie = attrs.join('; ');
  return true;
}

/**
 * Destination for the "Visit startengine.com" opt-out.
 *
 * The cookie written above only covers the current registrable domain, so the
 * flag is also carried on the URL: startengine.com must read
 * `?se_ca_optout=1` and write its own cookie, otherwise the opt-out only
 * sticks on the .ca side.
 */
export function buildOptOutUrl(base: string = STARTENGINE_COM_URL): string {
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}${CANADA_OPT_OUT_COOKIE}=1`;
}
